<template>
    <div class="customerTypeFields">

        <!-- סוג לקוח: one segmented control, the thumb slides between פרטי and עסקי (same control
             as the rent/sale toggle on the deal tab) -->
        <div class="d-flex align-items-center flex-wrap"
             style="gap:14px;margin-bottom:12px;">
            <div>
                <label class="form-label">סוג לקוח</label>
                <div class="customer-type-toggle"
                     :class="{ 'is-business': isBusiness }">
                    <span class="customer-type-toggle-thumb"></span>
                    <button type="button"
                            class="customer-type-toggle-btn"
                            :class="{ 'is-on': !isBusiness }"
                            @click="setType('private')">
                        פרטי
                    </button>
                    <button type="button"
                            class="customer-type-toggle-btn"
                            :class="{ 'is-on': isBusiness }"
                            @click="setType('business')">
                        עסקי
                    </button>
                </div>
            </div>
            <span v-if="isBusiness && hint"
                  style="color:#999;font-size:13px;align-self:flex-end;padding-bottom:8px;">
                {{ hint }}
            </span>
        </div>

        <!-- the business the documents are made out to -->
        <template v-if="isBusiness">

            <div class="row">

                <div class="col-md-3 col-sm-6">
                    <div class="mb-3">
                        <label class="form-label">שם העסק</label>
                        <input class="form-control"
                               v-model="customer.businessName">
                        <span class="formErrorText"
                              v-if="customer.businessErrors">
                            {{ customer.businessErrors.businessName }}
                        </span>
                    </div>
                </div>

                <div class="col-md-3 col-sm-6">
                    <div class="mb-3">
                        <label class="form-label">סוג העסק</label>
                        <Multiselect v-model="businessIdTypeOption"
                                     label="name"
                                     track-by="value"
                                     selectedLabel=""
                                     deselect-label=""
                                     selectLabel=""
                                     placeholder=""
                                     :allow-empty="false"
                                     :showNoOptions="false"
                                     :searchable="false"
                                     :options="$handlers.customerHandler.businessIdTypeOptions">
                            <template slot="noResult">לא נמצאו תוצאות</template>
                        </Multiselect>
                    </div>
                </div>

                <div class="col-md-3 col-sm-6">
                    <div class="mb-3">
                        <label class="form-label">{{ idLabel }}</label>
                        <input class="form-control businessNumberInput"
                               type="text"
                               inputmode="numeric"
                               style="direction:ltr;text-align:right;"
                               :value="customer.businessNumber"
                               @input="customer.businessNumber = $event.target.value.replace(/\D/g, '')">
                        <span class="formErrorText"
                              v-if="customer.businessErrors">
                            {{ customer.businessErrors.businessNumber }}
                        </span>
                    </div>
                </div>

            </div>

            <div class="row">

                <div class="col-md-3 col-sm-6">
                    <div class="mb-3">
                        <label class="form-label">רחוב</label>
                        <input class="form-control"
                               v-model="customer.businessAddressStreet">
                    </div>
                </div>

                <div class="col-md-3 col-sm-6">
                    <div class="mb-3">
                        <label class="form-label">מספר בית</label>
                        <input class="form-control"
                               v-model="customer.businessAddressNumber">
                    </div>
                </div>

                <div class="col-md-3 col-sm-6">
                    <div class="mb-3">
                        <label class="form-label">עיר</label>
                        <input class="form-control"
                               v-model="customer.businessAddressCity">
                    </div>
                </div>

                <div class="col-md-3 col-sm-6">
                    <div class="mb-3">
                        <label class="form-label">מיקוד</label>
                        <input class="form-control"
                               type="text"
                               inputmode="numeric"
                               v-model="customer.businessAddressMikud">
                        <span class="formErrorText"
                              v-if="customer.businessErrors">
                            {{ customer.businessErrors.businessAddressMikud }}
                        </span>
                    </div>
                </div>

            </div>

        </template>

    </div>
</template>

<script>

// סוג לקוח (פרטי / עסקי) + the business fields, edited straight on the customer object it gets:
// the create page, the add-customer modal, the customer page's contacts tab and the tax-document
// page all share it. Validation lives in CustomerHandler.validateBusinessDetails (errors land in
// customer.businessErrors), saving in CustomerHandler.saveBusinessDetails / the customer POST.

import Multiselect from 'vue-multiselect';

export default {
    name: 'CustomerTypeFields',

    props: {
        customer: { type: Object, required: true },
        hint: { type: String, default: '' },
    },

    created() {
        // a customer object that predates the business fields (or a bare {contacts}) still has to
        // be reactive on them
        this.$handlers.customerHandler.businessFields.forEach(field => {
            if (this.customer[field] === undefined)
                this.$set(this.customer, field, field == 'customerType' ? 'private' : (field == 'businessIdType' ? 'company' : null));
        });
    },

    computed: {

        isBusiness() {
            return this.$handlers.customerHandler.isBusinessCustomer(this.customer);
        },

        idLabel() {
            return this.$handlers.customerHandler.businessIdLabel(this.customer.businessIdType);
        },

        // the multiselect speaks in option objects, the customer stores the key
        businessIdTypeOption: {
            get() {
                let options = this.$handlers.customerHandler.businessIdTypeOptions;
                return options.find(option => option.value == (this.customer.businessIdType || 'company')) || options[0];
            },
            set(option) {
                this.$set(this.customer, 'businessIdType', option ? option.value : 'company');
            }
        }
    },

    methods: {

        setType(type) {

            this.$set(this.customer, 'customerType', type);

            if (type == 'business' && !this.customer.businessIdType)
                this.$set(this.customer, 'businessIdType', 'company');

            // errors of the other kind of customer are not this customer's errors
            if (this.customer.businessErrors)
                this.$set(this.customer, 'businessErrors', {});
        }
    },

    components: { Multiselect }
}
</script>

<style scoped>
.customer-type-toggle {
    position: relative;
    display: inline-flex;
    padding: 4px;
    background: #f1eff8;
    border-radius: 999px;
}

.customer-type-toggle-thumb {
    position: absolute;
    top: 4px;
    bottom: 4px;
    inset-inline-start: 4px;
    width: calc(50% - 4px);
    border-radius: 999px;
    background: #8d63da;
    transition: transform 0.25s ease;
}

/* RTL: פרטי sits on the right, so עסקי is one slot to the physical left */
.customer-type-toggle.is-business .customer-type-toggle-thumb {
    transform: translateX(-100%);
}

.customer-type-toggle-btn {
    position: relative;
    z-index: 1;
    flex: 1 1 0;
    min-width: 104px;
    border: none;
    background: none;
    border-radius: 999px;
    padding: 5px 16px;
    font-size: 14px;
    color: #777;
    white-space: nowrap;
    transition: color 0.25s ease;
}

.customer-type-toggle-btn.is-on {
    color: #fff;
}
</style>
